
import { useState, useEffect } from 'react';

export interface Appointment {
  id: string;
  patient: string;
  provider: string;
  time: string;
  duration: number;
  type: string;
  day: number;
  date: string;
  reason?: string;
  specialRequests?: string;
  status: "confirmed" | "cancelled" | "rescheduled" | "completed";
  // Added fields for audio triage integration
  triageId?: string;
  patientId?: string;
  urgency?: string;
}

const APPOINTMENTS_KEY = 'clinic-appointments';

const mockAppointments: Appointment[] = [
  { 
    id: "1", 
    patient: "Sarah Johnson", 
    provider: "Dr. Smith", 
    time: "9:00 AM", 
    duration: 30, 
    type: "Consultation", 
    day: 1, 
    date: "2024-12-23",
    status: "confirmed"
  },
  { 
    id: "2", 
    patient: "Michael Brown", 
    provider: "Dr. Wilson", 
    time: "9:30 AM", 
    duration: 30, 
    type: "Follow-up", 
    day: 1, 
    date: "2024-12-23",
    status: "confirmed"
  },
  { 
    id: "3", 
    patient: "Emma Davis", 
    provider: "Dr. Smith", 
    time: "10:00 AM", 
    duration: 60, 
    type: "Procedure", 
    day: 1, 
    date: "2024-12-23",
    status: "confirmed"
  },
  { 
    id: "4", 
    patient: "James Miller", 
    provider: "Dr. Johnson", 
    time: "2:00 PM", 
    duration: 30, 
    type: "Check-up", 
    day: 2, 
    date: "2024-12-24",
    status: "confirmed"
  },
  { 
    id: "5", 
    patient: "Lisa Wilson", 
    provider: "Dr. Smith", 
    time: "3:00 PM", 
    duration: 30, 
    type: "Consultation", 
    day: 3, 
    date: "2024-12-25",
    status: "confirmed"
  },
];

export function useAppointments() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // Load appointments from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(APPOINTMENTS_KEY);
      if (saved) {
        setAppointments(JSON.parse(saved));
      } else {
        // Initialize with mock data if no saved appointments
        setAppointments(mockAppointments);
        localStorage.setItem(APPOINTMENTS_KEY, JSON.stringify(mockAppointments));
      }
    } catch (error) {
      console.log('Error loading appointments:', error);
      setAppointments(mockAppointments);
    }
  }, []);

  // Save appointments to localStorage whenever they change
  useEffect(() => {
    if (appointments.length > 0) {
      try {
        localStorage.setItem(APPOINTMENTS_KEY, JSON.stringify(appointments));
      } catch (error) {
        console.log('Error saving appointments:', error);
      }
    }
  }, [appointments]);

  const addAppointment = (appointment: Omit<Appointment, 'id'>) => {
    const newAppointment: Appointment = {
      ...appointment,
      id: `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
    
    setAppointments(prev => [...prev, newAppointment]);
    return newAppointment;
  };

  const updateAppointment = (id: string, updates: Partial<Appointment>) => {
    setAppointments(prev => 
      prev.map(apt => apt.id === id ? { ...apt, ...updates } : apt)
    );
  };

  const deleteAppointment = (id: string) => {
    setAppointments(prev => prev.filter(apt => apt.id !== id));
  };

  return {
    appointments,
    addAppointment,
    updateAppointment,
    deleteAppointment,
  };
}
